"use client";

import { useEffect, useId, useRef, useState } from "react";

// A fire corona around the wheel built WITHOUT WebGL — a transparent WebGL canvas
// composites its empty pixels as an opaque white box on some Linux/GPU stacks
// (and behind the score bar's backdrop-blur), so this uses two reliably-composited
// layers instead:
//   1. An SVG flame ring distorted + boiled by an animated feTurbulence filter.
//   2. A 2D-canvas ember system that throws glowing sparks off the rim.
// Both honour prefers-reduced-motion. Colours come from the palette via props.

interface WheelFireProps {
  intensity: number; // ~0.7 (warm) … 1.0+ (inferno); scales spark rate + waviness
  colors: { core: string; mid: string; base: string };
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Embers are temporarily disabled (still being tuned). Flip to true to bring the
// 2D-canvas spark system back — the SVG flame corona is unaffected either way.
const SHOW_EMBERS = false;

interface Ember {
  x: number; // normalised 0..1 across the canvas
  y: number;
  vx: number; // units of canvas-width per second
  vy: number;
  life: number;
  ttl: number;
  size: number;
  mix: number; // 0 = base colour … 1 = white-hot core
}

export default function WheelFire({ intensity, colors }: WheelFireProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Live refs so changing tier/colour never restarts the loop.
  const intensityRef = useRef(intensity);
  const colorsRef = useRef(colors);
  intensityRef.current = intensity;
  colorsRef.current = colors;

  // Unique filter/gradient ids so multiple wheels (e.g. spectator) don't collide.
  const rawId = useId();
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, "");
  const fId = `flame-${uid}`;
  const gId = `fgrad-${uid}`;

  // Start with motion on (matches SSR); flip off after mount if the user asked
  // for reduced motion. Avoids a hydration mismatch.
  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) setAnimate(false);
    // Embers are off, or pure motion — either way, no canvas loop to run.
    if (!SHOW_EMBERS || reduce) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 1;
    let h = 1;
    const resize = () => {
      const cw = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const ch = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      w = canvas.width;
      h = canvas.height;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const embers: Ember[] = [];
    const RIM = 0.278; // disc radius within the inset:-40% canvas
    const MAX_R = 0.31; // cull radius — keeps embers a tight fringe on the rim

    const spawn = () => {
      // Around the rim but biased to the upper arc, so sparks plume off the
      // crown and the advance button below stays clear.
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.7;
      const r = RIM + (Math.random() - 0.3) * 0.02;
      const out = 0.004 + Math.random() * 0.008; // gentle outward drift
      embers.push({
        x: 0.5 + Math.cos(ang) * r,
        y: 0.5 + Math.sin(ang) * r,
        vx: Math.cos(ang) * out + (Math.random() - 0.5) * 0.012,
        vy: -(0.035 + Math.random() * 0.045), // gentle rise
        life: 0,
        // Short-lived so embers stay a tight fringe hugging the rim and never
        // drift off into the corners away from the wheel.
        ttl: 0.45 + Math.random() * 0.6,
        size: 1.6 + Math.random() * 2.6,
        mix: Math.random(),
      });
    };

    let raf = 0;
    let last = performance.now();
    let acc = 0;
    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const it = intensityRef.current;
      const [br, bg, bb] = hexToRgb(colorsRef.current.base);
      const [cr, cg, cb] = hexToRgb(colorsRef.current.core);

      acc += dt * (34 + it * 90); // spark rate scales with tier
      while (acc >= 1) {
        spawn();
        acc -= 1;
      }

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter"; // additive glow
      for (let i = embers.length - 1; i >= 0; i--) {
        const e = embers[i];
        e.life += dt;
        if (e.life >= e.ttl) {
          embers.splice(i, 1);
          continue;
        }
        e.vy -= dt * 0.08; // buoyancy
        e.vx *= 1 - dt * 0.8;
        e.x += e.vx * dt;
        e.y += e.vy * dt;

        // Hard-cull anything that drifts past a tight fringe of the rim, so
        // embers can only ever be a sparkle ring hugging the wheel — never
        // floating off toward the corners or up behind the score bar.
        const dx = e.x - 0.5;
        const dy = e.y - 0.5;
        if (dx * dx + dy * dy > MAX_R * MAX_R) {
          embers.splice(i, 1);
          continue;
        }

        const t = e.life / e.ttl;
        const alpha = Math.sin(t * Math.PI) * 0.95; // fade in then out
        const m = e.mix * (1 - t * 0.85); // cool toward base as it climbs
        const rr = Math.round(br + (cr - br) * m);
        const gg = Math.round(bg + (cg - bg) * m);
        const bbb = Math.round(bb + (cb - bb) * m);
        const px = e.x * w;
        const py = e.y * h;
        const sz = e.size * dpr * (1 - t * 0.4);
        const grd = ctx.createRadialGradient(px, py, 0, px, py, sz * 3);
        grd.addColorStop(0, `rgba(${rr},${gg},${bbb},${alpha})`);
        grd.addColorStop(1, `rgba(${rr},${gg},${bbb},0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(px, py, sz * 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Displacement grows with tier so higher streaks throw wilder tongues.
  const displace = 5 + intensity * 9;

  return (
    <>
      <svg
        className="aura-svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <radialGradient id={gId} gradientUnits="userSpaceOnUse" cx="50" cy="50" r="42">
            {/* Just inside the rim → transparent (never paints over the wheel). */}
            <stop offset="0.58" stopColor={colors.core} stopOpacity="0" />
            <stop offset="0.66" stopColor={colors.core} stopOpacity="0.95" />
            <stop offset="0.72" stopColor={colors.mid} stopOpacity="0.85" />
            <stop offset="0.82" stopColor={colors.base} stopOpacity="0.5" />
            <stop offset="0.95" stopColor={colors.base} stopOpacity="0" />
          </radialGradient>
          <filter
            id={fId}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.02 0.03"
              numOctaves={2}
              seed={7}
              result="n"
            >
              {animate && (
                <animate
                  attributeName="baseFrequency"
                  dur="5s"
                  values="0.02 0.03;0.022 0.06;0.02 0.03"
                  repeatCount="indefinite"
                />
              )}
            </feTurbulence>
            <feDisplacementMap
              in="SourceGraphic"
              in2="n"
              scale={displace}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
        <circle cx="50" cy="50" r="42" fill={`url(#${gId})`} filter={`url(#${fId})`} />
      </svg>

      {SHOW_EMBERS && (
        <canvas ref={canvasRef} className="aura-embers" aria-hidden />
      )}
    </>
  );
}
